import { cloneDeep } from 'lodash'

import { AskResponse, Citation, AzureSqlServerCodeExecResult } from '../../api'

export type ParsedAnswer = {
  citations: Citation[]
  markdownFormatText: string,
  plotly_data: AzureSqlServerCodeExecResult | null
}

export const enumerateCitations = (citations: Citation[]) => {
  const filepathMap = new Map()
  for (const citation of citations) {
    // Ensure citation is an object, not a string
    if (typeof citation !== 'object' || citation === null) {
      console.warn('Invalid citation format:', citation)
      continue
    }
    
    const { filepath } = citation
    let part_i = 1
    if (filepathMap.has(filepath)) {
      part_i = filepathMap.get(filepath) + 1
    }
    filepathMap.set(filepath, part_i)
    citation.part_index = part_i
  }
  return citations
}

export function parseAnswer(answer: AskResponse): ParsedAnswer {
  let answerText = answer.answer
  console.log('AnswerParser - Original answer text:', answerText)
  
  // Match both [doc1] format and 【3:0†source】 format
  const docLinks = answerText.match(/\[(doc\d\d?\d?)]/g)
  const unicodeLinks = answerText.match(/【(\d+):(\d+)†source】/g)
  const citationLinks = [...(docLinks || []), ...(unicodeLinks || [])]
  
  console.log('AnswerParser - Found citation links:', citationLinks)
  console.log('AnswerParser - Doc links:', docLinks)
  console.log('AnswerParser - Unicode links:', unicodeLinks)
  
  const lengthDocN = '[doc'.length

  // Ensure citations is an array and handle different formats
  let originalCitations: Citation[] = []
  if (Array.isArray(answer.citations)) {
    originalCitations = answer.citations.filter(citation => 
      typeof citation === 'object' && citation !== null
    ) as Citation[]
  } else if (answer.citations) {
    // Handle cases where citations might be a string (Python format or JSON)
    if (typeof answer.citations === 'string') {
      try {
        // First try to parse as JSON
        let parsedCitations = JSON.parse(answer.citations)
        if (Array.isArray(parsedCitations)) {
          originalCitations = parsedCitations.filter(citation => 
            typeof citation === 'object' && citation !== null
          ) as Citation[]
        } else {
          console.warn('Parsed citations is not an array:', parsedCitations)
          originalCitations = []
        }
      } catch (e) {
        try {
          // If JSON parsing fails, try to handle Python string format
          // Replace single quotes with double quotes for JSON compatibility
          const citationsString = answer.citations as string
          const jsonString = citationsString
            .replace(/'/g, '"')
            .replace(/True/g, 'true')
            .replace(/False/g, 'false')
            .replace(/None/g, 'null')
          
          const parsedCitations = JSON.parse(jsonString)
          if (Array.isArray(parsedCitations)) {
            originalCitations = parsedCitations.filter(citation => 
              typeof citation === 'object' && citation !== null
            ) as Citation[]
          } else {
            console.warn('Parsed citations is not an array:', parsedCitations)
            originalCitations = []
          }
        } catch (e2) {
          console.error('Failed to parse citations string:', answer.citations, e2)
          originalCitations = []
        }
      }
    } else {
      console.warn('Citations is not an array or string:', answer.citations)
      originalCitations = []
    }
  }

  let filteredCitations: Citation[] = []
  let citationReindex = 0
  citationLinks?.forEach(link => {
    console.log('AnswerParser - Processing citation link:', link)
    let citationIndex: string
    
    if (link.startsWith('[doc')) {
      // Handle [doc1] format
      citationIndex = link.slice(lengthDocN, link.length - 1)
      console.log('AnswerParser - Doc format citationIndex:', citationIndex)
    } else if (link.startsWith('【')) {
      // Handle 【3:0†source】 format - extract the first number
      const match = link.match(/【(\d+):/)
      citationIndex = match ? match[1] : '1'
      console.log('AnswerParser - Unicode format citationIndex:', citationIndex, 'from link:', link)
    } else {
      console.log('AnswerParser - Unknown format, skipping:', link)
      return // Skip unknown formats
    }
    
    // Find citation by matching the text field or by index
    let citation = originalCitations.find(c => (c as any).text === link) || 
                   originalCitations.find(c => c.id === citationIndex) ||
                   originalCitations[Number(citationIndex) - 1]
    
    if (!citation && originalCitations.length > 0) {
      // If we can't find by index, just use the first available citation
      citation = originalCitations[0]
      console.log('AnswerParser - Using fallback citation:', citation)
    }
    
    console.log('AnswerParser - Found citation for link', link, ':', citation)
    if (!filteredCitations.find(c => (c as any).text === link) && citation) {
      const replacementText = ` ^${++citationReindex}^ `
      console.log(`AnswerParser - Replacing "${link}" with "${replacementText}"`)
      answerText = answerText.replaceAll(link, replacementText)
      
      // Create a proper citation object
      const processedCitation = cloneDeep(citation) as Citation
      processedCitation.id = link // Use the full citation text as ID to ensure uniqueness
      processedCitation.reindex_id = citationReindex.toString()
      // Store the original text for reference
      ;(processedCitation as any).text = link
      
      // Extract URL from url_citation if it exists
      if ((citation as any).url_citation && (citation as any).url_citation.url) {
        const urlValue = (citation as any).url_citation.url
        // Check if it's a proper URL or just a document reference
        if (urlValue.startsWith('http://') || urlValue.startsWith('https://')) {
          processedCitation.url = urlValue
        } else if (urlValue.startsWith('doc_')) {
          // Convert doc reference to a fragment link for now
          processedCitation.url = `#citation-${urlValue}`
        } else {
          // For other cases, try to use as-is or create a fragment link
          processedCitation.url = urlValue.includes('://') ? urlValue : `#citation-${urlValue}`
        }
        processedCitation.title = (citation as any).url_citation.title || processedCitation.title
      }
      
      // Ensure we have content
      if (!processedCitation.content) {
        processedCitation.content = (citation as any).text || link
      }
      
      console.log('AnswerParser - Final processed citation:', processedCitation)
      filteredCitations.push(processedCitation)
    } else {
      console.log('AnswerParser - Skipping citation (already processed or not found):', link)
    }
  })

  filteredCitations = enumerateCitations(filteredCitations)
  
  return {
    citations: filteredCitations,
    markdownFormatText: answerText,
    plotly_data: answer.plotly_data
  }
}
